import * as React from "react";
import { Box, Card, Chip, Stack, Tooltip } from "@mui/material";
import Typography from "@mui/material/Typography";
import { PencilSimpleIcon } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

import { PhoneNumber, phoneNumbersKeys } from "@/hooks/api/use-phone-numbers";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { CustomTable } from "@/components/customTable";
import { TableActions } from "@/components/customTable/table-actions";
import { Column } from "@/components/customTable/types";
import { TableAnimation } from "@/components/table-animation/table-animation";

import { CreatePhoneNumber } from "./create";

export function PhoneNumbers(): React.JSX.Element {
	const [isCreatePhoneNumberDrawerOpen, setIsCreatePhoneNumberDrawerOpen] = React.useState(false);
	const [selectedPhoneNumberToCreate, setSelectedPhoneNumberToCreate] = React.useState<PhoneNumber | null>(null);

	const {
		items: phoneNumbers,
		isLoading,
		totalItems,
		refetchData: fetchPhoneNumbers,
	} = useCachedTableStateWithUrl({
		queryKey: phoneNumbersKeys.lists(),
		endpoint: "/phone-numbers",
	});

	const navigate = useNavigate();

	const columns: Column[] = React.useMemo(() => {
		return [
			{
				header: {
					id: "id",
					label: "ID",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "id",
			},
			{
				header: {
					id: "number",
					label: "Number",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "number",
			},
			{
				header: {
					id: "virtual",
					label: "Virtual Number",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "virtual",
				getCellValue: (row: any) => {
					return row.virtual ? (
						<Chip color="default" variant="outlined" size="small" label="Virtual" />
					) : (
						""
					);
				},
			},
			{
				header: {
					id: "countryCode",
					label: "Country Code",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "countryCode",
			},
			{
				header: {
					id: "campaign",
					label: "Campaign",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "campaign",
				getCellValue: (row: any) => {
					if (!row.campaign) return "";
					return (
						<Tooltip title="View Campaign" arrow>
							<Chip
								color="info"
								size="small"
								label={row.campaign?.title}
								onClick={() => navigate(`/campaigns/${row.campaign?.id}/view`)}
							/>
						</Tooltip>
					);
				},
			},
			{
				header: {
					id: "plugin",
					label: "Plugin",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "plugin",
				getCellValue: (row: any) => {
					return row.plugin ? <Chip color="default" variant="outlined" size="small" label={row.plugin?.title} /> : "";
				},
			},
			{
				header: {
					id: "concurrencyLimit",
					label: "Concurrency Limit",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "concurrencyLimit",
			},
			{
				header: {
					id: "enabled",
					label: "Enabled",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "enabled",
				getCellValue: (row: any) => {
					const isEnabled = row.enabled;

					if (!isEnabled) {
						return null;
					}

					return (
						<Chip
							// icon={<CheckCircleIcon color="var(--mui-palette-success-main)" weight="fill" />}
							label={"Enabled"}
							size="small"
							variant="filled"
							color="success"
						/>
					);
				},
			},

			{
				header: {
					id: "actions",
					label: "",
					width: 100,
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "actions",
				getCellValue: (row: any) => (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
						<TableActions
							actions={[
								{
									label: "Edit",
									icon: <PencilSimpleIcon width={20} height={20} weight="fill" />,
									onClick: () => {
										setSelectedPhoneNumberToCreate(row);
										setIsCreatePhoneNumberDrawerOpen(true);
									},
								},
							]}
						/>
					</Box>
				),
			},
		];
	}, []);

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack direction={"column"} spacing={3}>
						<Stack
							direction={{ xs: "column", sm: "row" }}
							spacing={3}
							sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
						>
							<Box sx={{ flex: "1 1 auto" }}>
								<Typography variant="h4">Phone Numbers Management</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
									Manage phone numbers
								</Typography>
							</Box>
							{/* <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
								<Button startIcon={<PlusIcon />} variant="contained" onClick={() => {}}>
									Add Phone Number
								</Button>
							</Box> */}
						</Stack>
					</Stack>
					<TableAnimation delay={0}>
						<Card>
							<CustomTable
								columns={columns}
								data={phoneNumbers}
								refetchData={fetchPhoneNumbers}
								isLoading={isLoading}
								totalItems={totalItems}
								filtersOptions={[]}
							/>
						</Card>
					</TableAnimation>
				</Stack>
			</Box>

			<CreatePhoneNumber
				phoneNumber={selectedPhoneNumberToCreate}
				isCreateDrawerOpen={isCreatePhoneNumberDrawerOpen}
				onCreateDrawerClose={() => {
					setIsCreatePhoneNumberDrawerOpen(false);
					setTimeout(() => {
						setSelectedPhoneNumberToCreate(null);
					}, 300);
				}}
			/>
		</React.Fragment>
	);
}
